import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";
import { DeskCallout } from "@/components/site/DeskCallout";
import { MarginCalculator } from "@/components/tools/MarginCalculator";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-margin-calculator";
const PAGE_URL = `${SITE}${PATH}`;
const TITLE = "Gold Margin Calculator: XAUUSD Leverage | XAU Pro Academy";
const DESC =
  "Free XAUUSD margin calculator. Enter lots, leverage and live gold price to get required margin per position, margin per lot and a full leverage table from 1:20 to 1:500.";

const LEVERAGES = [20, 50, 100, 200, 500];

const FAQS = [
  { q: "How much margin do I need for 1 lot of gold?", a: "Margin = (price x 100 oz) / leverage. At a gold price of $4,100 and 1:100 leverage, one standard lot needs about $4,100 of margin. At 1:500 it needs about $820, and at 1:20 about $20,500." },
  { q: "How do you calculate margin for XAUUSD?", a: "Required margin = (gold price x ounces per lot x lots) / leverage. Because gold is quoted in US dollars, the result is already in USD for a dollar account and no conversion is needed." },
  { q: "Does the gold price change the margin requirement?", a: "Yes. Margin scales directly with the live price. The same 1 lot at 1:100 leverage costs roughly 10% more margin when gold rises 10%. This is why margin checks should use the current price, not a fixed number." },
  { q: "What leverage do brokers offer on gold?", a: "Retail leverage on XAUUSD commonly ranges from 1:20 under EU and UK rules to 1:500 at offshore brokers. Higher leverage lowers the margin deposit but does not change the dollar risk of the position, which is set by the stop distance and lot size." },
  { q: "What is the difference between margin and risk?", a: "Margin is the deposit the broker locks to open the trade. Risk is what you lose if the stop is hit: stop distance x ounces. A position can need only $820 of margin while carrying $2,000 of risk. Size positions from risk, never from available margin." },
  { q: "What is a margin call on a gold trade?", a: "A margin call triggers when equity falls toward the broker's maintenance level, typically when floating losses consume most of the free margin. Gold's fast London and New York moves can close that gap quickly on oversized positions." },
];

const SECTIONS = [
  ["calculator", "Calculator"],
  ["table", "Margin table"],
  ["formula", "Formula"],
  ["risk", "Margin vs risk"],
  ["faq", "FAQ"],
] as const;

export const Route = createFileRoute("/gold-margin-calculator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold margin calculator, xauusd margin calculator, gold leverage calculator, how much margin for 1 lot gold, xauusd margin requirement, gold trading leverage" },
      { property: "og:title", content: "Gold Margin Calculator: XAUUSD Margin and Leverage" },
      { property: "og:description", content: "Compute the exact margin required for any XAUUSD position at any leverage, prefilled with the live gold price." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Gold Margin Calculator (XAUUSD)",
          url: PAGE_URL,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC, featureList: ["Required margin per lot", "Leverage comparison", "Free margin estimate"], browserRequirements: "Requires JavaScript. Modern HTML5 browser", softwareVersion: "2026.1", isAccessibleForFree: true, inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Gold Margin Calculator", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: GoldMarginCalculatorPage,
});

const usd = (x: number) =>
  (x < 0 ? "-$" : "$") + Math.abs(x).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (x: number, d = 1) => x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const TABLE_LOTS = [0.01, 0.1, 0.5, 1, 2];

function MarginTable({ price }: { price: number }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <caption className="sr-only">Gold margin required by lot size and leverage, 100 oz per lot</caption>
        <thead className="bg-card font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="p-3 text-left">Lots</th>
            <th className="p-3 text-right">Notional</th>
            {LEVERAGES.map((l) => (
              <th key={l} className="p-3 text-right">1:{l}</th>
            ))}
          </tr>
        </thead>
        <tbody className="font-mono">
          {TABLE_LOTS.map((lots) => {
            const notional = price * 100 * lots;
            return (
              <tr key={lots} className="border-t border-border">
                <td className="p-3">{lots.toFixed(2)}</td>
                <td className="p-3 text-right">{usd(notional)}</td>
                {LEVERAGES.map((l) => (
                  <td key={l} className={`p-3 text-right ${l === 100 ? "text-gold" : ""}`}>{usd(notional / l)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MarginTableLive() {
  const live = useGoldPrice(60_000);
  const price = live.price ?? 4100;
  return (
    <>
      <MarginTable price={price} />
      <p className="mt-2 text-xs text-muted-foreground">
        Computed at {live.price ? `live spot ${price.toFixed(2)}` : "a reference price of 4,100.00"}. Margin scales one to one with the gold price.
      </p>
    </>
  );
}

function LeverageDiagram() {
  const [lots, setLots] = useState(1);
  const price = 4100;
  const notional = price * 100 * lots;
  const maxMargin = notional / 20;
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 230" role="img" aria-labelledby="lv-t lv-d" className="w-full">
        <title id="lv-t">Margin deposit by leverage</title>
        <desc id="lv-d">The same gold position needs a much smaller margin deposit at higher leverage, while the notional exposure and risk stay identical.</desc>
        {LEVERAGES.map((l, i) => {
          const m = notional / l;
          const y = 14 + i * 40;
          const w = Math.max(4, (m / maxMargin) * 330);
          return (
            <g key={l}>
              <text x={0} y={y + 18} className="fill-muted-foreground" style={{ font: "12px monospace" }}>1:{l}</text>
              <rect x={70} y={y + 4} width={w} height={20} rx={3} className="fill-gold/25 stroke-gold" />
              <text x={78 + w} y={y + 19} className="fill-foreground" style={{ font: "600 12px monospace" }}>{usd(m)}</text>
            </g>
          );
        })}
        <text x={70} y={222} className="fill-muted-foreground" style={{ font: "11px monospace" }}>{`SAME POSITION: ${lots.toFixed(2)} LOTS = ${usd(notional)} NOTIONAL AT $${num(price, 0)}`}</text>
      </svg>
      <label className="mt-4 block">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Position: {lots.toFixed(2)} lots</span>
        <input type="range" min={0.1} max={5} step={0.1} value={lots} onChange={(e) => setLots(Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" aria-label="Position size in lots" />
      </label>
    </figure>
  );
}

function GoldMarginCalculatorPage() {
  return (
    <Layout>
      <article className="mx-auto max-w-5xl px-4 py-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Market Tools / XAUUSD</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">Gold Margin Calculator: XAUUSD Margin and Leverage</h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          One standard lot of gold at <strong className="text-foreground">1:100 leverage</strong> needs roughly <strong className="text-foreground">$4,100 of margin</strong> at a $4,100 price. Margin moves one to one with the live price, so this calculator is prefilled with the current spot quote and updates the full leverage table instantly.
        </p>

        <nav aria-label="On this page" className="sticky top-16 z-20 -mx-4 mt-8 border-y border-border bg-background/95 px-4 py-2 backdrop-blur">
          <ul className="flex gap-4 overflow-x-auto font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {SECTIONS.map(([id, label]) => (
              <li key={id}><a href={`#${id}`} className="whitespace-nowrap hover:text-gold">{label}</a></li>
            ))}
          </ul>
        </nav>

        <section id="calculator" className="scroll-mt-32 pt-10">
          <h2 className="text-2xl font-semibold">XAUUSD margin calculator</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Enter the position and leverage, then read the required margin, notional exposure and how the margin deposit compares to the actual risk at your stop.</p>
          <MarginCalculator />
        </section>

        <section id="table" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Gold margin table by leverage</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Required margin in USD at 100 oz per lot. The highlighted column is the most common retail leverage.</p>
          <MarginTableLive />
        </section>

        <section id="formula" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">The formula</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {[
              ["Required margin", "(Price x oz per lot x lots) / leverage", "(4,100 x 100 x 1) / 100 = $4,100"],
              ["Notional value", "Price x oz per lot x lots", "4,100 x 100 x 1 = $410,000"],
              ["Margin per 0.01 lot", "Margin per lot / 100", "$4,100 / 100 = $41"],
            ].map(([t, f, e]) => (
              <div key={t} className="rounded-lg border border-border bg-card p-4">
                <div className="font-mono text-[11px] uppercase tracking-wider text-gold">{t}</div>
                <div className="mt-2 font-mono text-sm text-foreground">{f}</div>
                <div className="mt-2 font-mono text-xs text-muted-foreground">{e}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Gold is priced in US dollars, so no conversion applies to a USD account. Some brokers add a margin buffer or raise requirements around high impact news; check your broker's margin schedule.</p>
        </section>

        <section id="risk" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Margin is a deposit, not your risk</h2>
          <div className="mt-4 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 leading-relaxed text-muted-foreground">
              <p>Leverage only changes the deposit the broker locks. A 1 lot position carries the same $100 per dollar of price movement whether the account runs 1:20 or 1:500. The risk of the trade is set by the stop distance and the lot size, never by the leverage setting.</p>
              <p><strong className="text-foreground">Size from risk, not from margin.</strong> Decide the dollar amount you are prepared to lose, divide by the stop distance, and let the margin requirement be whatever it is. Accounts that size from available margin are one volatility spike away from a margin call.</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild variant="gold"><Link to="/position-size-calculator">Size the position from risk <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                <Button asChild variant="outline"><Link to="/gold-pip-calculator">Gold pip value calculator</Link></Button>
              </div>
            </div>
            <LeverageDiagram />
          </div>
        </section>

        <section id="faq" className="scroll-mt-32 pt-14">
          <FAQ items={FAQS} />
        </section>

        <DeskCallout
          className="mt-14"
          eyebrow="Margin is the deposit. Risk is the stop."
          title="The desk fixes risk at 1R before leverage enters the picture."
          body="Leverage changes the deposit, never the loss at the stop. OTAS-01 defines a structural stop on every setup, so position size follows risk rather than available margin."
        />

        <aside className="mt-6 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">Related references</h2>
          <ul className="mt-3 grid gap-2 text-sm md:grid-cols-2">
            <li><Link to="/position-size-calculator" className="text-gold hover:underline">Gold position size calculator</Link></li>
            <li><Link to="/gold-pip-calculator" className="text-gold hover:underline">Gold pip value calculator</Link></li>
            <li><Link to="/what-is-xauusd" className="text-gold hover:underline">What is XAUUSD: symbol and contract sizes</Link></li>
            <li><Link to="/best-prop-firms-for-gold" className="text-gold hover:underline">Best prop firms for gold trading</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">Educational tool. Figures exclude commission, swap, slippage and broker margin buffers. Trading leveraged products carries substantial risk.</p>
        </aside>
      </article>
    </Layout>
  );
}
